import type { TextProps } from './types';
import { SOLID_COLOR, sanitizeGradient, sanitizePaint, type Gradient } from './paint';

/**
 * Text effects beyond the basic TextProps: gradient fills, stacked outlines,
 * glow, 3D extrusion, offset echoes, letter case, underline… Stored in the
 * clip's `ext` (ClipExt.textFx) so they need no database migration. Every
 * field is optional; absent means off. Pixel values are at project
 * resolution, like TextProps.
 */
/** A gradient fill (see paint.ts). */
export type TextGradient = Gradient;

export interface TextOutline {
  color: string;
  /** Width in px outside the outline inside it. */
  width: number;
}

export interface TextEffects {
  /** Gradient fill of the letters, replacing the solid colour. */
  fill?: TextGradient;
  /** Draw no fill: outline (and effects) only. */
  hollow?: boolean;
  /** Gradient of the main outline (TextProps.strokeWidth), replacing its colour. */
  stroke?: TextGradient;
  /** Further outlines around the main one, innermost first. */
  outlines?: TextOutline[];
  /** Soft light around the letters. strength = number of passes (1..5). */
  glow?: { color: string; blur: number; strength: number };
  /** 3D block extrusion behind the letters. With `fade`, the far end darkens. */
  extrude?: { color: string; depth: number; angle: number; fade?: boolean };
  /** Offset copies behind the letters (glitch, retro duplicates). */
  echoes?: { color: string; x: number; y: number }[];
  /** Letter case of the shown text. */
  case?: 'upper' | 'lower' | 'title';
  underline?: boolean;
  strike?: boolean;
  /** Background box: corner radius, gradient fill (instead of TextProps.background) and border. */
  box?: { radius?: number; fill?: TextGradient; border?: string; borderWidth?: number };
}

export const TEXT_CASES = ['upper', 'lower', 'title'] as const;
const MAX_STOPS = 8;
const MAX_LAYERS = 6;

const color = (v: unknown, fallback: string): string => (typeof v === 'string' && v.length <= 40 && SOLID_COLOR.test(v.trim()) ? v.trim().toLowerCase() : fallback);
/** A colour or a gradient (CSS string, see paint.ts). */
const paint = (v: unknown, fallback: string): string => sanitizePaint(v) ?? fallback;
const num = (v: unknown, lo: number, hi: number, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback;
const obj = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null);

/** Keeps known effects with valid values only; undefined when nothing is on. */
export function sanitizeTextFx(raw: unknown): TextEffects | undefined {
  const r = obj(raw);
  if (!r) return undefined;
  const out: TextEffects = {};
  const fill = sanitizeGradient(r.fill);
  if (fill) out.fill = fill;
  if (r.hollow === true) out.hollow = true;
  const stroke = sanitizeGradient(r.stroke);
  if (stroke) out.stroke = stroke;
  if (Array.isArray(r.outlines)) {
    const outlines = (r.outlines as unknown[]).slice(0, MAX_LAYERS).flatMap((o) => {
      const x = obj(o);
      const width = num(x?.width, 0, 200, 0);
      return x && width > 0 ? [{ color: paint(x.color, '#000000'), width }] : [];
    });
    if (outlines.length) out.outlines = outlines;
  }
  const glow = obj(r.glow);
  if (glow) {
    const blur = num(glow.blur, 0, 200, 20);
    if (blur > 0) out.glow = { color: color(glow.color, '#ffffff'), blur, strength: Math.round(num(glow.strength, 1, 5, 2)) };
  }
  const ex = obj(r.extrude);
  if (ex) {
    const depth = Math.round(num(ex.depth, 0, 200, 10));
    if (depth > 0) out.extrude = { color: paint(ex.color, '#000000'), depth, angle: num(ex.angle, -360, 360, 45), ...(ex.fade === true ? { fade: true } : {}) };
  }
  if (Array.isArray(r.echoes)) {
    const echoes = (r.echoes as unknown[]).slice(0, MAX_LAYERS).flatMap((e) => {
      const x = obj(e);
      return x ? [{ color: paint(x.color, '#ff00ff'), x: num(x.x, -500, 500, 0), y: num(x.y, -500, 500, 0) }] : [];
    });
    if (echoes.length) out.echoes = echoes;
  }
  if ((TEXT_CASES as readonly unknown[]).includes(r.case)) out.case = r.case as TextEffects['case'];
  if (r.underline === true) out.underline = true;
  if (r.strike === true) out.strike = true;
  const box = obj(r.box);
  if (box) {
    const b: NonNullable<TextEffects['box']> = {};
    const radius = num(box.radius, 0, 1000, 0);
    if (radius > 0) b.radius = radius;
    const bf = sanitizeGradient(box.fill);
    if (bf) b.fill = bf;
    const bw = num(box.borderWidth, 0, 100, 0);
    if (bw > 0) {
      b.borderWidth = bw;
      b.border = paint(box.border, '#ffffff');
    }
    if (Object.keys(b).length) out.box = b;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Scales every pixel-valued effect by `k` (preview / export resolution). */
export function scaleTextFx(fx: TextEffects | undefined, k: number): TextEffects | undefined {
  if (!fx || k === 1) return fx;
  return {
    ...fx,
    ...(fx.outlines ? { outlines: fx.outlines.map((o) => ({ ...o, width: o.width * k })) } : {}),
    ...(fx.glow ? { glow: { ...fx.glow, blur: fx.glow.blur * k } } : {}),
    ...(fx.extrude ? { extrude: { ...fx.extrude, depth: Math.max(1, Math.round(fx.extrude.depth * k)) } } : {}),
    ...(fx.echoes ? { echoes: fx.echoes.map((e) => ({ ...e, x: e.x * k, y: e.y * k })) } : {}),
    ...(fx.box ? { box: { ...fx.box, radius: (fx.box.radius ?? 0) * k, borderWidth: (fx.box.borderWidth ?? 0) * k } } : {}),
  };
}

/** The text as shown with the effects' letter case. */
export function applyCase(text: string, fx: TextEffects | undefined): string {
  switch (fx?.case) {
    case 'upper':
      return text.toUpperCase();
    case 'lower':
      return text.toLowerCase();
    case 'title':
      return text.toLowerCase().replace(/((?:^|[\s\-(])['"“‘]?)(\p{L})/gu, (_m, a: string, b: string) => a + b.toUpperCase());
    default:
      return text;
  }
}

/** Total width of the outlines around the main one. */
export function outlinesWidth(fx: TextEffects | undefined): number {
  return fx?.outlines?.reduce((n, o) => n + o.width, 0) ?? 0;
}

/** Extra room the effects need around the text box, px. */
export function textFxMargin(fx: TextEffects | undefined): number {
  if (!fx) return 0;
  let m = outlinesWidth(fx);
  if (fx.glow) m = Math.max(m, fx.glow.blur * 2);
  if (fx.extrude) m += fx.extrude.depth;
  for (const e of fx.echoes ?? []) m = Math.max(m, Math.abs(e.x) + outlinesWidth(fx), Math.abs(e.y) + outlinesWidth(fx));
  if (fx.box?.borderWidth) m = Math.max(m, fx.box.borderWidth / 2);
  return Math.ceil(m);
}

/** Two-stop helper for presets. */
const lin = (angle: number, ...colors: string[]): TextGradient => ({
  type: 'linear',
  angle,
  stops: colors.map((c, i) => ({ at: colors.length > 1 ? i / (colors.length - 1) : 0, color: c })),
});

export interface TextFxPreset {
  id: string;
  label: string;
  /** Basic text settings the look sets (font, size and content stay). */
  text: Partial<TextProps>;
  fx: TextEffects | null;
}

const NO_SHADOW: Partial<TextProps> = { shadowBlur: 0, shadowX: 0, shadowY: 0 };

/** Ready-made text looks: each sets the colours, outline, shadow and effects. */
export const TEXT_FX_PRESETS: TextFxPreset[] = [
  { id: 'plain', label: 'Plain', text: { color: '#ffffff', strokeWidth: 0, shadowColor: 'rgba(0,0,0,0.5)', shadowBlur: 12, shadowX: 0, shadowY: 4, background: '' }, fx: null },
  {
    id: 'gold',
    label: 'Gold',
    text: { color: '#f5c542', strokeColor: '#5c3d00', strokeWidth: 2, shadowColor: 'rgba(0,0,0,0.6)', shadowBlur: 10, shadowX: 0, shadowY: 4, background: '' },
    fx: { fill: { type: 'linear', angle: 90, stops: [{ at: 0, color: '#fff6c2' }, { at: 0.45, color: '#f5c542' }, { at: 0.55, color: '#b8860b' }, { at: 1, color: '#ffe08a' }] } },
  },
  {
    id: 'chrome',
    label: 'Chrome',
    text: { color: '#d0d6dc', strokeColor: '#1f2328', strokeWidth: 2, shadowColor: 'rgba(0,0,0,0.6)', shadowBlur: 8, shadowX: 0, shadowY: 3, background: '' },
    fx: { fill: { type: 'linear', angle: 90, stops: [{ at: 0, color: '#ffffff' }, { at: 0.48, color: '#aeb7c1' }, { at: 0.52, color: '#3e4650' }, { at: 1, color: '#e9eef3' }] } },
  },
  {
    id: 'neon',
    label: 'Neon',
    text: { color: '#ffffff', strokeColor: '#ff2bd6', strokeWidth: 3, ...NO_SHADOW, background: '' },
    fx: { glow: { color: '#ff2bd6', blur: 24, strength: 3 } },
  },
  {
    id: 'neon-outline',
    label: 'Neon tube',
    text: { color: '#ffffff', strokeColor: '#33f0ff', strokeWidth: 3, ...NO_SHADOW, background: '' },
    fx: { hollow: true, glow: { color: '#33f0ff', blur: 18, strength: 3 } },
  },
  {
    id: 'fire',
    label: 'Fire',
    text: { color: '#ff7a00', strokeWidth: 0, ...NO_SHADOW, background: '' },
    fx: { fill: lin(90, '#fff200', '#ff7a00', '#d10000'), glow: { color: '#ff4500', blur: 20, strength: 2 } },
  },
  {
    id: 'ice',
    label: 'Ice',
    text: { color: '#7fd3ff', strokeColor: '#ffffff', strokeWidth: 2, shadowColor: 'rgba(0,40,90,0.6)', shadowBlur: 10, shadowX: 0, shadowY: 3, background: '' },
    fx: { fill: lin(90, '#ffffff', '#7fd3ff', '#2a6fff'), glow: { color: '#9be7ff', blur: 14, strength: 1 } },
  },
  {
    id: 'sunset',
    label: 'Sunset',
    text: { color: '#ff512f', strokeWidth: 0, shadowColor: 'rgba(0,0,0,0.45)', shadowBlur: 10, shadowX: 0, shadowY: 4, background: '' },
    fx: { fill: lin(0, '#ff9a3c', '#ff512f', '#dd2476') },
  },
  {
    id: 'rainbow',
    label: 'Rainbow',
    text: { color: '#ffffff', strokeColor: '#111111', strokeWidth: 3, ...NO_SHADOW, background: '' },
    fx: { fill: lin(0, '#ff3b30', '#ff9500', '#ffcc00', '#34c759', '#0a84ff', '#af52de') },
  },
  {
    id: 'retro',
    label: 'Retro 3D',
    text: { color: '#ffd23f', strokeColor: '#1b1b3a', strokeWidth: 3, ...NO_SHADOW, background: '' },
    fx: { extrude: { color: '#ee4266', depth: 12, angle: 45 }, outlines: [{ color: '#1b1b3a', width: 2 }] },
  },
  {
    id: 'block',
    label: '3D block',
    text: { color: '#ffffff', strokeWidth: 0, shadowColor: 'rgba(0,0,0,0.5)', shadowBlur: 16, shadowX: 0, shadowY: 8, background: '' },
    fx: { extrude: { color: '#3b82f6', depth: 16, angle: 60, fade: true } },
  },
  {
    id: 'comic',
    label: 'Comic',
    text: { color: '#ffe600', strokeColor: '#000000', strokeWidth: 5, ...NO_SHADOW, background: '' },
    fx: { outlines: [{ color: '#ffffff', width: 5 }], extrude: { color: '#000000', depth: 8, angle: 45 }, case: 'upper' },
  },
  {
    id: 'sticker',
    label: 'Sticker',
    text: { color: '#ff3d7f', strokeColor: '#ffffff', strokeWidth: 8, shadowColor: 'rgba(0,0,0,0.35)', shadowBlur: 12, shadowX: 0, shadowY: 6, background: '' },
    fx: null,
  },
  {
    id: 'glitch',
    label: 'Glitch',
    text: { color: '#ffffff', strokeWidth: 0, ...NO_SHADOW, background: '' },
    fx: { echoes: [{ color: '#00f0ff', x: -5, y: 0 }, { color: '#ff0055', x: 5, y: 0 }], case: 'upper' },
  },
  {
    id: 'outline',
    label: 'Outline',
    text: { color: '#ffffff', strokeColor: '#ffffff', strokeWidth: 2, ...NO_SHADOW, background: '' },
    fx: { hollow: true },
  },
  {
    id: 'pill',
    label: 'Pill',
    text: { color: '#ffffff', strokeWidth: 0, ...NO_SHADOW, background: '#7c3aed', padding: 28 },
    fx: { box: { radius: 999, fill: lin(0, '#7c3aed', '#db2777') } },
  },
  {
    id: 'label',
    label: 'Label',
    text: { color: '#111111', strokeWidth: 0, ...NO_SHADOW, background: '#ffffff', padding: 20 },
    fx: { box: { radius: 12, border: '#111111', borderWidth: 4 }, case: 'upper' },
  },
];
